import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuClock, LuNewspaper } from "react-icons/lu";
import PublicNav, { PublicFooter } from "../components/PublicNav";
import SEO from "../components/SEO";
import { Btn, Card, Badge, Skel, Empty, Note } from "../components/Ui";
import { blogPosts as localPosts } from "./blogData";
import "./blog.css";

const BLOG_API = "https://blog-post-response-streaming-v2-980069659423.us-east1.run.app";

function fmtDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function excerptOf(post) {
  const raw = post?.excerpt || post?.summary || post?.description || "";
  const text = String(raw).replace(/<[^>]*>/g, "").trim();
  if (!text) return "";
  if (text.length <= 180) return text;
  return `${text.slice(0, 177).trimEnd()}...`;
}

function readingTimeOf(post) {
  const v = post?.reading_time ?? post?.read_time;
  if (!v) return "";
  return typeof v === "number" ? `${v} min read` : String(v);
}

/* locally authored articles render without any network call; API posts (if the
   service returns any) are appended after them */
const LOCAL_AS_POSTS = localPosts.map((p) => ({
  slug: p.id,
  title: p.title,
  excerpt: p.description,
  reading_time: p.readTime,
  published_at: p.date,
  tag: p.category,
  audience: p.audience,
  local: true,
}));

/* ItemList of the locally authored articles — gives the index page crawlable
   structure pointing at each article URL */
const BLOG_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "CareerSavvy articles",
  itemListElement: localPosts.map((p, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: `https://careersavvy.ai/blogDetailed?slug=${p.id}`,
    name: p.title,
  })),
};

export default function Blog() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState(LOCAL_AS_POSTS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getAllPosts = useCallback(async () => {
    setError("");
    try {
      const response = await fetch(BLOG_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operation: "list",
          status: "published",
          tag: "technical interview",
          author: "Career Coach",
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const remote = Array.isArray(data.posts) ? data.posts : [];
        if (remote.length) setPosts([...LOCAL_AS_POSTS, ...remote]);
      } else {
        console.error("Error", await response.text());
      }
    } catch (err) {
      console.error("Network error (local articles still shown):", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getAllPosts();
  }, [getAllPosts]);

  const openPost = (post) => {
    if (!post?.slug) return;
    navigate(`/blogDetailed?slug=${post.slug}`);
  };

  const onCardKey = (e, post) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openPost(post);
    }
  };

  const feature = posts[0];
  const rest = posts.slice(1);
  const featureExcerpt = feature ? excerptOf(feature) : "";
  const featureRead = feature ? readingTimeOf(feature) : "";

  return (
    <div className="csx">
      <SEO
        title="Career insights & job-search tips"
        description="Actionable career advice, resume tips, and interview guidance from CareerSavvy to help you land your next role."
        canonicalPath="/blog"
        jsonLd={BLOG_JSON_LD}
        breadcrumbs={[{ label: "Blog", path: "/blog" }]}
      />
      <div className="csx-page">
        <PublicNav />

        <main>
          <section className="land-section">
            <div className="wrap">
              <div className="stack-2 mb-4">
                <h1 className="t-h1">Career insights</h1>
                <p className="t-sm">Practical guides for interviews, resumes, and getting hired.</p>
              </div>

              {error ? (
                <div className="stack-3" style={{ maxWidth: 560 }}>
                  <Note tone="danger">{error}</Note>
                  <div>
                    <Btn variant="quiet" onClick={getAllPosts}>
                      Try again
                    </Btn>
                  </div>
                </div>
              ) : loading ? (
                <>
                  <Card pad={false} className="blog-feature" aria-hidden="true">
                    <div className="stack-3">
                      <Skel w={110} h={22} r={999} />
                      <Skel w="72%" h={30} />
                      <Skel w="90%" h={14} />
                      <Skel w="46%" h={12} />
                    </div>
                  </Card>
                  <div className="grid-2 mt-3">
                    {[0, 1, 2, 3].map((i) => (
                      <Card key={i} aria-hidden="true">
                        <div className="stack-2">
                          <Skel w="60%" h={17} />
                          <Skel w="92%" h={13} />
                          <Skel w="38%" h={12} />
                        </div>
                      </Card>
                    ))}
                  </div>
                </>
              ) : posts.length === 0 ? (
                <Empty
                  icon={<LuNewspaper size={26} />}
                  title="No posts yet"
                  sub="New articles are on the way. Check back soon."
                />
              ) : (
                <>
                  {/* Featured post */}
                  <Card
                    pad={false}
                    hover
                    className="blog-feature"
                    role="link"
                    tabIndex={0}
                    onClick={() => openPost(feature)}
                    onKeyDown={(e) => onCardKey(e, feature)}
                  >
                    <div className="stack-3">
                      {feature.tags?.[0] ? (
                        <div className="row">
                          <Badge tone="accent">{feature.tags[0]}</Badge>
                        </div>
                      ) : null}
                      <h2 className="blog-feature-title">{feature.title}</h2>
                      {featureExcerpt ? <p className="t-sm blog-excerpt">{featureExcerpt}</p> : null}
                      <div className="meta-row t-xs">
                        {feature.author ? <span>{feature.author}</span> : null}
                        <span className="meta-dot">{fmtDate(feature.created_at)}</span>
                        {featureRead ? (
                          <span className="meta-dot">
                            <LuClock size={13} /> {featureRead}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </Card>

                  {/* Remaining posts */}
                  {rest.length > 0 ? (
                    <>
                      <div className="mt-4 mb-2">
                        <span className="t-label">More posts</span>
                      </div>
                      <div className="grid-2">
                        {rest.map((post) => {
                          const ex = excerptOf(post);
                          return (
                            <Card
                              key={post.id || post.slug}
                              hover
                              className="blog-item"
                              role="link"
                              tabIndex={0}
                              onClick={() => openPost(post)}
                              onKeyDown={(e) => onCardKey(e, post)}
                            >
                              <div className="stack-2">
                                {post.tags?.[0] ? <span className="t-label">{post.tags[0]}</span> : null}
                                <h3 className="t-h3 blog-clamp-2">{post.title}</h3>
                                {ex ? <p className="t-sm blog-excerpt">{ex}</p> : null}
                                <div className="meta-row t-xs">
                                  {post.author ? <span>{post.author}</span> : null}
                                  <span className="meta-dot">{fmtDate(post.created_at)}</span>
                                </div>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    </>
                  ) : null}
                </>
              )}
            </div>
          </section>
        </main>

        <PublicFooter />
      </div>
    </div>
  );
}
